import React, { useState } from 'react';
import { useAuth } from '../App';
import axios from 'axios';
import { toast } from 'sonner';
import UniversalAddressInput from './UniversalAddressInput';
import { Send, X } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const QuickPostForm = ({ onSuccess, onCancel }) => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    industry: ['Tyre Service'],
    job_specification: '',
    location: '',
    location_formatted_address: '',
    location_lat: null,
    location_lng: null,
    location_place_id: null,
    location_manual_entry: false,
    quoted_price: '',
    urgency_level: 'normal'
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleIndustryChange = (industry) => {
    setFormData(prev => ({
      ...prev,
      industry: prev.industry.includes(industry)
        ? prev.industry.filter(i => i !== industry)
        : [...prev.industry, industry]
    }));
  };

  const handleAddressSelect = (addressData) => {
    setFormData(prev => ({
      ...prev,
      location_formatted_address: addressData.formatted_address,
      location_lat: addressData.lat,
      location_lng: addressData.lng,
      location_place_id: addressData.place_id,
      location_manual_entry: addressData.manual_entry
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const jobData = {
        ...formData,
        quoted_price: parseFloat(formData.quoted_price)
      };

      const response = await axios.post(`${API}/jobs`, jobData);
      toast.success('Job posted successfully! SMS notifications sent to receivers.');
      
      // Call success callback with the created job
      onSuccess(response.data);
      
    } catch (error) {
      console.error('Failed to create job:', error);
      toast.error('Failed to post job');
    } finally {
      setLoading(false);
    }
  };

  const industryOptions = [
    'Tyre Service',
    'Mechanics',
    'Haulage',
    'Plumbing',
    'Electrical',
    'Other'
  ];

  return (
    <div className="jop-card p-6">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-xl font-semibold text-gray-900">Quick Post Job</h2>
          <p className="text-sm text-gray-600 mt-1">
            Get your overflow job live in under 30 seconds
          </p>
        </div>
        <button
          onClick={onCancel}
          className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100"
        >
          <X className="h-5 w-5" />
        </button>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Job Title *
            </label>
            <input
              type="text"
              name="title"
              value={formData.title}
              onChange={handleChange}
              className="jop-input"
              placeholder="e.g., Mobile tyre fitting"
              required
            />
          </div>

          <div>
            <UniversalAddressInput
              value={formData.location}
              onChange={(value) => handleChange({ target: { name: 'location', value } })}
              onAddressSelect={handleAddressSelect}
              placeholder="e.g., Newcastle, NSW or 123 Main Street, Newcastle NSW"
              label="Location (Suburb/Town or Full Address)"
              required={true}
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Industry *
          </label>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
            {industryOptions.map((industry) => (
              <label key={industry} className="flex items-center">
                <input
                  type="checkbox"
                  checked={formData.industry.includes(industry)}
                  onChange={() => handleIndustryChange(industry)}
                  className="rounded border-gray-300 text-amber-600 shadow-sm focus:border-amber-500 focus:ring-amber-500"
                />
                <span className="ml-2 text-sm text-gray-700">{industry}</span>
              </label>
            ))}
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Job Specification *
          </label>
          <textarea
            name="job_specification"
            value={formData.job_specification}
            onChange={handleChange}
            rows="3"
            className="jop-input resize-none"
            placeholder="e.g., 4x tyres 235/75R15, mobile service required"
            required
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Quoted Price ($) *
            </label>
            <input
              type="number"
              name="quoted_price"
              value={formData.quoted_price}
              onChange={handleChange}
              step="0.01"
              min="0"
              className="jop-input"
              placeholder="0.00"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Urgency *
            </label>
            <select
              name="urgency_level"
              value={formData.urgency_level}
              onChange={handleChange}
              className="jop-select"
              required
            >
              <option value="normal">Normal - Next 24-48 hours</option>
              <option value="asap">ASAP - Within 4 hours</option>
              <option value="after_hours">After Hours - Emergency</option>
            </select>
          </div>
        </div>

        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
          <h3 className="text-sm font-medium text-amber-800 mb-2">Quick Post Benefits:</h3>
          <ul className="text-xs text-amber-700 space-y-1">
            <li>• Job goes live immediately</li>
            <li>• SMS alerts sent to all available receivers</li>
            <li>• You can add detailed info after posting</li>
            <li>• First to accept gets the job</li>
          </ul>
        </div>

        <div className="flex space-x-4">
          <button
            type="submit"
            disabled={loading}
            className="jop-button flex-1 flex items-center justify-center"
          >
            {loading ? (
              <div className="spinner" />
            ) : (
              <>
                <Send className="h-4 w-4 mr-2" />
                Post Job & Send SMS
              </>
            )}
          </button>
          <button
            type="button"
            onClick={onCancel}
            className="jop-button-secondary"
          >
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
};

export default QuickPostForm;